import * as Sentry from '@sentry/react';

/* eslint-disable no-console */
let sentryInitialized = false;

/**
 * Check if Sentry should be used in the current environment
 */
export function shouldUseSentry(): boolean {
  return process.env.NODE_ENV !== 'development';
}

/**
 * Initialize Sentry with comprehensive configuration
 * This should be called once at app startup
 */
export function initSentry() {
  if (sentryInitialized) {
    return;
  }

  // Completely disable Sentry in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[SENTRY_DEBUG] Sentry disabled in development environment');
    return;
  }

  // Only initialize if DSN is provided
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();

  // Debug logging for production troubleshooting
  console.log('[SENTRY_DEBUG] Initializing Sentry...', {
    hasDsn: !!sentryDsn,
    dsnLength: sentryDsn?.length || 0,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    nodeEnv: process.env.NODE_ENV,
  });

  // Sentry initialization debug info (removed console statements for production)

  if (!sentryDsn) {
    // eslint-disable-next-line no-console
    console.log('Sentry DSN not found, skipping initialization');
    return;
  }

  // Validate DSN format
  if (sentryDsn.includes('\n') || sentryDsn.includes(' ')) {
    // eslint-disable-next-line no-console
    console.error('Invalid Sentry DSN format - contains line breaks or spaces');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy-first configuration
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        // Elements to ignore in replay
        ignore: ['.sentry-ignore', '.no-capture', '[data-sentry-ignore]'],
        // Don't unmask anything - keep everything masked for privacy
        unmask: [],
        // Don't unblock any media - keep everything blocked
        unblock: [],
      }),
    ],

    // Error Monitoring - Always capture all errors
    sampleRate: 1.0, // 100% error capture in all environments - critical for monitoring

    // Performance Monitoring
    // Restored sampling rates for adequate monitoring coverage
    tracesSampleRate:
      process.env.REACT_APP_SENTRY_ENVIRONMENT === 'staging'
        ? 0.5 // 50% for staging - good balance of coverage and performance
        : process.env.REACT_APP_ENVIRONMENT === 'production'
          ? 0.3 // 30% for production - adequate coverage without overwhelming
          : 1.0, // 100% for development

    // Which URLs to track
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/fresh-rebuild\.netlify\.app/,
      /^https:\/\/.*\.glyyd\.com/,
      /^https:\/\/.*\.glyyd\.net/,
      /^https:\/\/.*\.vercel\.app/,
    ],

    // Session Replay sample rates - restored for better debugging
    replaysSessionSampleRate: 0.1, // 10% of sessions - adequate for debugging
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors - critical for debugging

    // Before sending event to Sentry
    beforeSend(event, hint) {
      sanitizeSentryEventForSend(event);

      // Don't send events from development unless explicitly enabled
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SENTRY_ENABLE_IN_DEV) {
        return null;
      }

      // Filter out specific errors
      const error = hint.originalException as Error | undefined;

      // Ignore ResizeObserver errors (common and not actionable)
      if (error && error.message && error.message.includes('ResizeObserver loop limit exceeded')) {
        return null;
      }

      // Don't ignore network errors or serverless function errors - these are important
      // Only ignore specific development network errors
      if (
        process.env.NODE_ENV === 'development' &&
        error &&
        error.message &&
        (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))
      ) {
        return null;
      }

      // Ensure serverless function errors are captured
      if (error && error.message && error.message.includes('FUNCTION_INVOCATION_FAILED')) {
        // Add additional context for serverless function errors
        event.tags = {
          ...event.tags,
          error_type: 'serverless_function_failure',
          platform: 'vercel',
        };

        // Add extra context
        event.extra = {
          ...event.extra,
          serverless_error: true,
          function_name: extractFunctionName(error.message),
          error_id: extractErrorId(error.message),
        };
      }

      return event;
    },

    // Breadcrumb filtering
    beforeBreadcrumb(breadcrumb, _hint) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'warning') {
        return null;
      }

      // Sanitize breadcrumb data
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeBreadcrumbData(breadcrumb.data);
      }

      return breadcrumb;
    },

    // URLs to track errors from
    allowUrls: [
      /https:\/\/fresh-rebuild\.netlify\.app/,
      /https:\/\/.*\.glyyd\.com/,
      /https:\/\/.*\.glyyd\.net/,
      /https:\/\/.*\.vercel\.app/,
      /http:\/\/localhost:\d+/,
    ],

    // Errors to ignore - but NOT serverless function errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Facebook related
      'fb_xd_fragment',
      // Safari specific
      'Non-Error promise rejection captured',
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Network errors (handle these differently)
      /Failed to fetch/,
      /NetworkError/,
      /Load failed/,
      // Resize observer (noisy and not actionable)
      'ResizeObserver loop limit exceeded',
      // React development warnings
      'Warning: ',
      // BUT DO NOT ignore serverless function errors
    ],
  });

  sentryInitialized = true;
  // Sentry initialization complete - debug logging removed for production builds
}

/**
 * Emit a controlled startup exception to verify bootstrap capture in Sentry.
 * Enabled via REACT_APP_SENTRY_STARTUP_SMOKE_TEST=1 for staging validation.
 */
export function captureStartupSmokeTestException(
  stage: 'bootstrap' | 'first-render' = 'bootstrap'
) {
  if (!shouldUseSentry()) return;

  Sentry.captureException(new Error(`[SENTRY_STARTUP_SMOKE_TEST] Controlled ${stage} exception`), {
    level: 'warning',
    tags: {
      smoke_test: 'startup',
      stage,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'unknown',
    },
  });
}

/**
 * GLY-226: strip direct identifiers before events leave the browser.
 */
export function sanitizeSentryEventForSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
    delete event.user.name;
  }

  if (event.request) {
    delete event.request.cookies;

    if (event.request.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
      delete event.request.headers['X-CSRF-Token'];
      delete event.request.headers['Cookie'];
      delete event.request.headers['cookie'];
    }

    if (event.request.query_string && typeof event.request.query_string === 'string') {
      event.request.query_string = sanitizeQueryString(event.request.query_string);
    }
  }

  if (event.exception?.values) {
    event.exception.values.forEach((exception) => {
      if (exception.value) {
        exception.value = sanitizeErrorMessage(exception.value);
      }
    });
  }

  if (event.message && typeof event.message === 'string') {
    event.message = sanitizeErrorMessage(event.message);
  }

  return event;
}

/**
 * Set pseudonymous user context for Sentry (id + role tag; no email).
 */
export function setSentryUser(user: { id: string; role?: string } | null) {
  if (!shouldUseSentry()) return;

  if (user) {
    Sentry.setUser({ id: user.id });
    if (user.role) {
      Sentry.setTag('user_role', user.role);
    }
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add custom context for rowing-specific features
 */
export function setRowingContext(context: {
  isAthlete?: boolean;
  isCoach?: boolean;
  hasProfile?: boolean;
  schoolsViewed?: number;
}) {
  if (!shouldUseSentry()) return;

  Sentry.setContext('rowing', context);
}

/**
 * Track custom transactions for key user actions
 */
export function trackTransaction(name: string, op: string, callback: () => Promise<any>) {
  if (!shouldUseSentry()) {
    return callback();
  }

  return Sentry.startSpan({ name, op }, () => {
    return callback();
  });
}

/**
 * Add breadcrumb for user actions
 */
export function addActionBreadcrumb(category: string, message: string, data?: Record<string, any>) {
  if (!shouldUseSentry()) return;

  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data: data ? sanitizeBreadcrumbData(data) : undefined,
  });
}

/**
 * Capture serverless function errors specifically
 */
export function captureServerlessError(error: Error, context?: Record<string, any>) {
  if (!shouldUseSentry()) return;

  Sentry.captureException(error, {
    tags: {
      error_type: 'serverless_function_failure',
      platform: 'vercel',
    },
    contexts: {
      serverless: {
        function_name: extractFunctionName(error.message),
        error_id: extractErrorId(error.message),
        ...context,
      },
    },
  });
}

/**
 * Capture upload errors with specific context
 */
export function captureUploadError(error: Error, context?: Record<string, any>) {
  if (!shouldUseSentry()) return;

  Sentry.captureException(error, {
    tags: {
      error_type: 'file_upload_failure',
      operation: 'telemetry_upload',
    },
    contexts: {
      upload: {
        file_type: context?.fileType,
        file_size: context?.fileSize,
        user_id: context?.userId,
        ...context,
      },
    },
  });
}

// Helper functions for sanitization
function sanitizeQueryString(queryString: string): string {
  const params = new URLSearchParams(queryString);
  const sensitiveParams = ['token', 'api_key', 'secret', 'password', 'auth'];

  sensitiveParams.forEach((param) => {
    if (params.has(param)) {
      params.set(param, '[REDACTED]');
    }
  });

  return params.toString();
}

function sanitizeErrorMessage(message: string): string {
  // Remove email addresses
  message = message.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi, '[EMAIL]');

  // Remove potential API keys or tokens (long alphanumeric strings)
  message = message.replace(/[a-zA-Z0-9]{32,}/g, '[REDACTED]');

  // Remove credit card numbers
  message = message.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[CARD]');

  return message;
}

function sanitizeBreadcrumbData(data: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'email', 'ssn', 'credit_card'];

  for (const [key, value] of Object.entries(data)) {
    if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string' && value.length > 100) {
      // Truncate long strings
      sanitized[key] = value.substring(0, 100) + '...';
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

// Helper functions for extracting serverless error information
function extractFunctionName(errorMessage: string): string {
  // Extract function name from error message like "FUNCTION_INVOCATION_FAILED fra1::47m7b-1754303733099-c3724961be83"
  const match = errorMessage.match(/FUNCTION_INVOCATION_FAILED\s+([^:]+)::/);
  return match ? match[1] : 'unknown';
}

function extractErrorId(errorMessage: string): string {
  // Extract error ID from error message like "FUNCTION_INVOCATION_FAILED fra1::47m7b-1754303733099-c3724961be83"
  const match = errorMessage.match(/FUNCTION_INVOCATION_FAILED\s+[^:]+::[^-]+-(\d+)-([a-f0-9]+)/);
  return match ? `${match[1]}-${match[2]}` : 'unknown';
}
